import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import ApiCall from "../../utils/apicall";
import { clearAccessToken } from "../../utils/authToken";
import {
  Autocomplete,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Card,
  MenuItem,
  Chip,
  Checkbox,
  FormControlLabel,
  Collapse,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Iconify from "../../components/iconify";
import KPI_CHECKLISTS from "../../config/kpiConfig";

const STATIONS = ["Vendor Control", "Cut", "Fit", "Weld", "Paint", "Ship"];

function KioskView() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { loginUser } = useSelector((state) => state.userSlice);
  const scanInputRef = useRef(null);
  const inactivityTimer = useRef(null);

  // State
  const [job, setJob] = useState(null);
  const [currentStation, setCurrentStation] = useState(STATIONS[0]);
  const [scanInput, setScanInput] = useState("");
  const [fabItems, setFabItems] = useState([]);   // all piece marks for autocomplete
  const [currentItem, setCurrentItem] = useState(null);
  const [linkedHeats, setLinkedHeats] = useState([]);
  const [loading, setLoading] = useState(false);
  // Codex WARNING fix: per-action pending state so operators on slow
  // shop-floor networks can't double-tap Start/Complete/Flag and trigger
  // duplicate task submissions. null | "start" | "complete" | "flag".
  const [actionPending, setActionPending] = useState(null);
  const [error, setError] = useState(null);
  const [flagIssueModal, setFlagIssueModal] = useState(false);
  const [issueReason, setIssueReason] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [taskStatus, setTaskStatus] = useState("pending");
  // Codex #63 P1 (round 2): every status read carries a generation number. Navigation, a new read and
  // every action bump it, so only the latest read may write the status; an older read that returns
  // late (e.g. a "pending" from before Start) is dropped and cannot re-offer Start.
  const statusGenRef = useRef(0);
  const setStatusNow = (value) => {
    statusGenRef.current += 1;
    setTaskStatus(value);
  };
  // W-21: the server id of the work in process, so Pause / Resume are recorded against it.
  const [taskId, setTaskId] = useState(null);

  // KPI checklist state — map of index → checked (resets per item+station)
  const [kpiChecked, setKpiChecked] = useState({});
  const [kpiExpanded, setKpiExpanded] = useState(false);
  // W-20: the checklist is opened before Complete, so a saved "clear" means it was looked at.
  const [kpiReviewed, setKpiReviewed] = useState(false);

  const kpiItems = KPI_CHECKLISTS[currentStation] || [];
  const needsReview = kpiItems.length > 0 && !kpiReviewed;
  const kpiCheckedCount = Object.values(kpiChecked).filter(Boolean).length;

  // Defect-tracking color logic: 0 = green (clear), 1-9 = yellow (attention), 10 = red (hold)
  const kpiColor =
    kpiCheckedCount === 0
      ? "#4caf50"                       // green — no defects
      : kpiCheckedCount < kpiItems.length
        ? "#ff9800"                     // yellow/amber — some defects
        : "#f44336";                    // red — full hold

  const kpiStatusLabel =
    kpiCheckedCount === 0
      ? "Clear"
      : kpiCheckedCount < kpiItems.length
        ? "Attention"
        : "HOLD";

  const handleKpiToggle = (idx) => {
    setKpiChecked((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Reset KPI state when item or station changes
  useEffect(() => {
    setKpiChecked({});
    setKpiExpanded(false);
    setKpiReviewed(false);
  }, [currentItem?.id, currentStation]);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Inactivity auto-logout — 15 minutes of no interaction
  useEffect(() => {
    const TIMEOUT_MS = 15 * 60 * 1000;

    const resetTimer = () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        clearAccessToken();
        navigate("/login");
      }, TIMEOUT_MS);
    };

    const events = ["mousemove", "keydown", "touchstart", "click", "scroll"];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    resetTimer(); // start on mount

    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [navigate]);

  // Fetch job
  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await ApiCall.get(`/job/${jobId}`);
        setJob(res.data?.data?.job);
      } catch (err) {
        setError("Failed to load job");
      }
    };

    if (jobId) fetchJob();
  }, [jobId]);

  // Pre-load all fabricated items for this job — powers piece-mark autocomplete
  useEffect(() => {
    const fetchFabItems = async () => {
      try {
        const res = await ApiCall.get(`/fabricated-items/job/${jobId}`);
        const items = res.data?.data || [];
        setFabItems(items);
      } catch {
        // Non-fatal — falls back to blind scan entry
        setFabItems([]);
      }
    };
    if (jobId) fetchFabItems();
  }, [jobId]);

  // Fetch the latest task status for a given item + station from the server
  const fetchTaskStatus = async (itemId, station) => {
    try {
      const res = await ApiCall.get(
        `/task?fabricatedItemId=${itemId}&station=${encodeURIComponent(station)}`
      );
      const tasks = res.data?.data?.tasks?.data || res.data?.data || [];
      // One row per piece and station (2026-10-02): the row in process wins; otherwise the newest of the
      // finished row and a pending row decides. A pending row's id is kept so Start moves THAT row.
      const mine = tasks.filter((t) => t.fabricatedItemId === itemId && t.station === station);
      const newest = (sts) => mine.filter((t) => sts.includes(t.status)).sort((a, b) => b.id - a.id)[0];
      const inProcess = newest(["in_process"]);
      if (inProcess) {
        // W-21: work in process may be paused (a pause recorded on the server).
        try {
          const br = await ApiCall.get(`/task/${inProcess.id}/pause-state`);
          return { status: br.data?.data?.paused === true ? "paused" : "in_process", taskId: inProcess.id };
        } catch {
          // Codex #63 P2: an unread pause state is not shown as "in process"; the operator retries.
          return { status: "unknown", taskId: null };
        }
      }
      const done = newest(["to_inspect", "approved"]);
      const pending = newest(["pending"]);
      if (done && (!pending || done.id > pending.id)) return { status: "to_inspect", taskId: done.id };
      return { status: "pending", taskId: pending ? pending.id : null };
    } catch {
      // Codex #63 P1: a failed read is never shown as "pending". Start would otherwise create a second
      // row next to an existing pending row. No action is offered until the status is read.
      return { status: "unknown", taskId: null };
    }
  };

  // Re-read the status of the piece and station on screen (Retry, and after a refused action).
  const reloadStatus = async () => {
    if (!currentItem) return;
    const item = currentItem;
    const station = currentStation;
    const gen = ++statusGenRef.current;
    setTaskStatus("loading");
    setTaskId(null);
    const { status, taskId: id } = await fetchTaskStatus(item.id, station);
    if (statusGenRef.current === gen && currentItemRef.current?.id === item.id && currentStationRef.current === station) {
      setTaskStatus(status);
      setTaskId(id);
    }
  };

  const currentItemRef = useRef(null);
  const currentStationRef = useRef(null);
  currentItemRef.current = currentItem;
  currentStationRef.current = currentStation;

  // BUG-S01: When operator switches stations, fetch actual status from server instead of assuming pending
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!currentItem) return;
    let cancelled = false;
    const gen = ++statusGenRef.current;
    setTaskStatus("loading");
    setTaskId(null);
    fetchTaskStatus(currentItem.id, currentStation).then(({ status, taskId: id }) => {
      if (!cancelled && statusGenRef.current === gen) { setTaskStatus(status); setTaskId(id); }
    });
    return () => { cancelled = true; };
  }, [currentStation, currentItem]);

  // Focus scan input on mount and when item changes
  useEffect(() => {
    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [currentItem]);

  // Handle scan input — accepts optional override mark for autocomplete selection
  const handleScanSubmit = async (markOverride) => {
    const mark = (markOverride || scanInput).trim();
    if (!mark) return;

    setLoading(true);
    setError(null);
    try {
      // Fetch fabricated item by mark
      const itemRes = await ApiCall.get(
        `/fabricated-items/job/${jobId}?mark=${encodeURIComponent(mark)}`
      );
      const item = itemRes.data?.data?.[0];

      if (!item) {
        setError("Item not found");
        setScanInput("");
        return;
      }

      // Hydrate task status from server — prevents duplicate in_process records
      // when a piece is already started by another operator or terminal.
      // W-21 (Codex): cleared at once; the effect on [currentStation, currentItem] reads the status
      // and applies it only if the piece and station are still the ones on screen.
      setStatusNow("loading");
      setTaskId(null);
      setCurrentItem(item);

      // Fetch linked heats
      try {
        const heatsRes = await ApiCall.get(`/heat/job/${jobId}?itemId=${item.id}`);
        setLinkedHeats(heatsRes.data?.data || []);
      } catch {
        setLinkedHeats([]);
      }

      setScanInput("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load item");
      setScanInput("");
    } finally {
      setLoading(false);
    }
  };

  // Mark complete — only callable when in_process
  const handleMarkComplete = async () => {
    statusGenRef.current += 1; // Codex #63 P1: a read started before this action must not overwrite its result.
    if (actionPending) return; // Codex fix: prevent double-submit
    if (!currentItem || taskStatus !== "in_process") return;
    if (needsReview) {
      setError("Open the QCI checklist and tick any defects you found before marking this work complete.");
      return;
    }
    if (!taskId) {
      setError("This work was not found on the server. Scan the piece again.");
      return;
    }
    setActionPending("complete");
    try {
      // One row per piece and station (2026-10-02): Complete moves the work row itself to inspection
      // (PUT /api/task/:id/station-step) instead of creating a second row. The server records who
      // completed it, checks the QCI checklist, and refuses HOLD (recorded).
      await ApiCall.put(`/task/${taskId}/station-step`, {
        step: "complete",
        // W-20: each ticked item is a flagged defect. HOLD (every item ticked) is refused and recorded.
        ...(kpiItems.length > 0
          ? { qciChecklist: { flagged: kpiItems.filter((_, idx) => !!kpiChecked[idx]) } }
          : {}),
      });
      setStatusNow("to_inspect");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to mark complete");
      reloadStatus();
    } finally {
      setActionPending(null);
    }
  };

  // W-21: Pause and Resume are recorded on the server (server time, the signed-in worker).
  const handlePauseResume = async (kind) => {
    statusGenRef.current += 1; // Codex #63 P1: a read started before this action must not overwrite its result.
    if (actionPending) return;
    const from = kind === "pause" ? "in_process" : "paused";
    if (!currentItem || taskStatus !== from) return;
    if (!taskId) {
      setError("This work was not found on the server. Scan the piece again.");
      return;
    }
    setActionPending(kind);
    try {
      await ApiCall.put(`/task/${taskId}/${kind}`, {});
      setStatusNow(kind === "pause" ? "paused" : "in_process");
    } catch (err) {
      setError(err.response?.data?.message || (kind === "pause" ? "Failed to pause" : "Failed to resume"));
      reloadStatus();
    } finally {
      setActionPending(null);
    }
  };

  // Start task — only callable when pending
  const handleStartTask = async () => {
    statusGenRef.current += 1; // Codex #63 P1: a read started before this action must not overwrite its result.
    if (actionPending) return; // Codex fix: prevent double-submit
    if (!currentItem || taskStatus !== "pending") return;
    setActionPending("start");
    try {
      // One row per piece and station (2026-10-02): a pending row for this piece and station is started
      // in place (PUT /api/task/:id/station-step); only when there is none is a row created.
      if (taskId) {
        await ApiCall.put(`/task/${taskId}/station-step`, { step: "start" });
        setStatusNow("in_process");
        return;
      }
      const res = await ApiCall.post(`/task`, {
        fabricatedItemId: currentItem.id,
        jobId: jobId,
        userId: loginUser.decodedToken.id,
        status: "in_process",
        estimatedHour: 0,
        station: currentStation,
        startedBy: loginUser.decodedToken.id,
      });
      setTaskId(res.data?.data?.task?.id ?? null);
      setStatusNow("in_process");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to start task");
      reloadStatus();
    } finally {
      setActionPending(null);
    }
  };

  // Flag issue
  const handleFlagIssue = async () => {
    if (actionPending) return; // Codex fix: prevent double-submit
    if (!currentItem || !issueReason.trim()) return;
    setActionPending("flag");
    try {
      await ApiCall.post(`/ncr`, {
        fabricatedItemId: currentItem.id,
        description: issueReason,
        station: currentStation,
        reportedBy: loginUser.decodedToken.id,
      });
      setFlagIssueModal(false);
      setIssueReason("");
      setError("Issue flagged successfully");
      setTimeout(() => setError(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to flag issue");
    } finally {
      setActionPending(null);
    }
  };

  // Logout
  const handleLogout = () => {
    clearAccessToken();
    navigate("/login");
  };

  const timeString = currentTime.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const dateString = currentTime.toLocaleDateString();

  // Resolve the display mark — API may return name or mark field
  const displayMark = (item) => item?.name || item?.mark || "—";

  // BUG-007: filterOptions — exact match first, then starts-with, then rest
  const filterOptions = (options, { inputValue }) => {
    const lower = inputValue.toLowerCase().trim();
    if (!lower) return options;
    const exact = options.filter((o) => o.toLowerCase() === lower);
    const starts = options.filter(
      (o) => o.toLowerCase().startsWith(lower) && o.toLowerCase() !== lower
    );
    const rest = options.filter((o) => !o.toLowerCase().startsWith(lower));
    return [...exact, ...starts, ...rest];
  };

  // BUG-001: PaperComponent for reliable dark dropdown styling
  const DarkDropdownPaper = (props) => (
    <Paper
      {...props}
      sx={{
        backgroundColor: "#0d1b2e",
        border: "1px solid rgba(255,255,255,0.2)",
        color: "white",
        "& .MuiAutocomplete-option": {
          fontSize: "1.4rem",
          padding: "12px 20px",
          color: "white",
          '&[aria-selected="true"]': {
            backgroundColor: "rgba(25, 118, 210, 0.25)",
          },
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.08)",
          },
        },
        "& .MuiAutocomplete-noOptions": {
          color: "rgba(255,255,255,0.5)",
        },
      }}
    />
  );

  return (
    <div>
      <Helmet>
        <title>Kiosk View | SFM</title>
      </Helmet>

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#0f172a",
          color: "white",
          display: "flex",
          flexDirection: "column",
          padding: 2,
        }}
      >
        {/* Header — BUG-S02: sticky so it stays visible while scrolling */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            bgcolor: "#0f172a",
            mb: 3,
            pb: 2,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <div>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {job?.name || "Loading..."}
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.6)">
              Shop Floor — {dateString} {timeString}
            </Typography>
          </div>

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              select
              value={currentStation}
              onChange={(e) => {
                // W-21 (Codex): no action may use the previous station's task while the new one is read.
                setStatusNow(currentItem ? "loading" : "pending");
                setTaskId(null);
                setCurrentStation(e.target.value);
              }}
              disabled={actionPending !== null}
              variant="filled"
              size="small"
              sx={{
                width: 150,
                bgcolor: "rgba(255,255,255,0.1)",
                "& .MuiSelect-select": { color: "white" },
                "& .MuiSvgIcon-root": { color: "white" },
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      bgcolor: "#0d1b2e",
                      border: "1px solid rgba(255,255,255,0.2)",
                      "& .MuiMenuItem-root": {
                        color: "white",
                        fontSize: "1rem",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                        "&.Mui-selected": { bgcolor: "rgba(25, 118, 210, 0.25)" },
                      },
                    },
                  },
                },
              }}
            >
              {STATIONS.map((station) => (
                <MenuItem key={station} value={station}>
                  {station}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="text"
              color="inherit"
              onClick={handleLogout}
              sx={{ fontWeight: "bold" }}
            >
              {/* BUG-S05: decodedToken has firstName/lastName, not name */}
              {loginUser?.decodedToken?.firstName} {loginUser?.decodedToken?.lastName}
            </Button>
            <Button variant="outlined" color="error" onClick={handleLogout}>
              Logout
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Paper
            sx={{
              p: 2,
              mb: 2,
              bgcolor: error.includes("successfully")
                ? "rgba(76, 175, 80, 0.2)"
                : "rgba(244, 67, 54, 0.2)",
              border: `1px solid ${
                error.includes("successfully") ? "#4caf50" : "#f44336"
              }`,
            }}
          >
            <Typography color={error.includes("successfully") ? "success.light" : "error.light"}>
              {error}
            </Typography>
          </Paper>
        )}

        {/* Central Content Area */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          {!currentItem ? (
            // Scan Input
            <Stack alignItems="center" spacing={3} sx={{ mb: 4 }}>
              <div style={{ textAlign: "center" }}>
                <Typography variant="h4" sx={{ mb: 0.5 }}>
                  Ready at {currentStation}
                </Typography>
                <Typography variant="body1" color="rgba(255,255,255,0.6)">
                  Scan the barcode on your piece label, or type the mark number below.
                </Typography>
              </div>

              {/* BUG-001 fix: PaperComponent | BUG-002 fix: reduced placeholder font | BUG-007 fix: filterOptions */}
              <Autocomplete
                freeSolo
                disablePortal
                options={fabItems.map((fi) => fi.name || fi.mark || "")}
                inputValue={scanInput}
                onInputChange={(_, val) => setScanInput(val)}
                onChange={(_, val) => {
                  if (val) {
                    setScanInput(val);
                    handleScanSubmit(val);
                  }
                }}
                disabled={loading}
                sx={{ width: "100%", maxWidth: 600 }}
                PaperComponent={DarkDropdownPaper}
                filterOptions={filterOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={scanInputRef}
                    placeholder="Type or scan piece mark"
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === "Enter") handleScanSubmit();
                    }}
                    helperText={
                      fabItems.length > 0
                        ? `${fabItems.length} piece marks loaded — type or select`
                        : "Type piece mark and press Enter"
                    }
                    FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.5)", textAlign: "center" } }}
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: "2rem",
                        padding: "20px",
                        textAlign: "center",
                        color: "white",
                      },
                      // BUG-002: smaller font for placeholder so it doesn't overflow
                      "& .MuiInputBase-input::placeholder": {
                        color: "rgba(255,255,255,0.35)",
                        opacity: 1,
                        fontSize: "1.2rem",
                        textAlign: "center",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255,255,255,0.2)",
                        borderWidth: 2,
                      },
                      "& legend": { display: "none" },
                      "& fieldset": { top: 0 },
                      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255,255,255,0.4)",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1976d2",
                      },
                    }}
                  />
                )}
              />
              <Button
                variant="contained"
                onClick={handleScanSubmit}
                disabled={!scanInput.trim() || loading}
                sx={{ width: 200, height: 60, fontSize: "1.1rem" }}
              >
                {loading ? "Looking up..." : "Pull Work Card"}
              </Button>
            </Stack>
          ) : (
            // Item Card
            <Stack alignItems="center" spacing={3} sx={{ mb: 4 }}>
              {/* Item Mark - Large */}
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", letterSpacing: 2, textTransform: "uppercase" }}>
                  Subcomponent
                </Typography>
                {/* BUG-004 fix: use displayMark helper to handle name vs mark field */}
                <Typography variant="h2" sx={{ fontWeight: "bold", color: "#1976d2", lineHeight: 1.2 }}>
                  {displayMark(currentItem)}
                </Typography>
              </Box>

              {/* Item Details Card */}
              <Card
                sx={{
                  width: "100%",
                  maxWidth: 500,
                  p: 3,
                  bgcolor: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="rgba(255,255,255,0.6)">
                      Quantity
                    </Typography>
                    <Typography variant="h6" sx={{ color: "white" }}>
                      {currentItem.quantity ?? "—"}
                    </Typography>
                  </Box>

                  {/* BUG-009 fix: station label on its own line (display block), chip below with proper spacing */}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "rgba(255,255,255,0.6)",
                        mb: 1,
                        letterSpacing: 0.5,
                      }}
                    >
                      Station: {currentStation}
                    </Typography>
                    {/* BUG-006 fix: always filled variant; custom bg for pending state */}
                    <Chip
                      label={
                        taskStatus === "in_process" ? "In Progress"
                        : taskStatus === "paused" ? "Paused"
                        : taskStatus === "loading" ? "Reading…"
                        : taskStatus === "unknown" ? "Status not read"
                        : taskStatus === "to_inspect" ? "Ready to Inspect"
                        : "Pending"
                      }
                      color={
                        taskStatus === "to_inspect" ? "success"
                        : taskStatus === "in_process" ? "warning"
                        : taskStatus === "paused" ? "info"
                        : "default"
                      }
                      variant="filled"
                      sx={{
                        color: "white",
                        backgroundColor:
                          taskStatus === "pending" || !taskStatus
                            ? "#4A6FA5"
                            : undefined,
                        fontWeight: "bold",
                        fontSize: "0.95rem",
                        px: 1,
                      }}
                    />
                  </Box>

                  {linkedHeats.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="rgba(255,255,255,0.6)">
                        Linked Heats
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                        {linkedHeats.map((heat) => (
                          <Chip
                            key={heat.id}
                            label={heat.heatNumber}
                            size="small"
                            variant="outlined"
                            sx={{ color: "rgba(255,255,255,0.8)", borderColor: "rgba(255,255,255,0.3)" }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Card>

              {/* ── QCI Checklist (soft advisory) ── */}
              {kpiItems.length > 0 && (
                <Box sx={{ width: "100%", maxWidth: 500 }}>
                  {/* Header row */}
                  <Box
                    onClick={() => { setKpiExpanded((v) => !v); setKpiReviewed(true); }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      bgcolor: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: 1.5,
                      px: 2,
                      py: 1.2,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Iconify icon="eva:checkmark-square-2-outline" sx={{ color: "#4fc3f7", fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: "white", fontWeight: 600 }}>
                        QCI Checklist — {currentStation}
                      </Typography>
                      <Chip
                        label={`${kpiCheckedCount} / ${kpiItems.length} — ${kpiStatusLabel}`}
                        size="small"
                        sx={{
                          bgcolor:
                            kpiCheckedCount === 0
                              ? "rgba(76,175,80,0.2)"
                              : kpiCheckedCount < kpiItems.length
                                ? "rgba(255,152,0,0.2)"
                                : "rgba(244,67,54,0.25)",
                          color: kpiColor,
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          letterSpacing: kpiCheckedCount === kpiItems.length ? 1 : 0,
                        }}
                      />
                    </Stack>
                    <ExpandMoreIcon
                      sx={{
                        color: "rgba(255,255,255,0.5)",
                        transition: "transform 0.2s",
                        transform: kpiExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                        fontSize: 18,
                      }}
                    />
                  </Box>

                  {/* Progress bar — green→yellow→red as defects increase */}
                  <LinearProgress
                    variant="determinate"
                    value={kpiItems.length ? (kpiCheckedCount / kpiItems.length) * 100 : 0}
                    sx={{
                      height: 4,
                      borderRadius: "0 0 4px 4px",
                      bgcolor: "rgba(255,255,255,0.08)",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: kpiColor,
                        transition: "background-color 0.3s, transform 0.3s",
                      },
                    }}
                  />

                  {/* Checklist items */}
                  <Collapse in={kpiExpanded} timeout={200}>
                    <Box
                      sx={{
                        mt: 0.5,
                        bgcolor: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderTop: "none",
                        borderRadius: "0 0 8px 8px",
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mb: 0.5, color: "rgba(255,255,255,0.7)" }}
                      >
                        Tick each defect you found. Leave the rest clear. The checklist is saved when you mark this work complete.
                      </Typography>
                      {kpiItems.map((item, idx) => (
                        <FormControlLabel
                          key={idx}
                          control={
                            <Checkbox
                              checked={!!kpiChecked[idx]}
                              onChange={() => handleKpiToggle(idx)}
                              size="small"
                              sx={{
                                color: "rgba(255,255,255,0.3)",
                                // W-20: a tick is a flagged defect, so it shows red (not a green "OK").
                                "&.Mui-checked": { color: "#f44336" },
                                py: 0.5,
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              sx={{
                                color: kpiChecked[idx]
                                  ? "#ef9a9a"
                                  : "rgba(255,255,255,0.85)",
                                fontWeight: kpiChecked[idx] ? 600 : 400,
                                fontSize: "0.82rem",
                                lineHeight: 1.4,
                                transition: "color 0.15s",
                              }}
                            >
                              {item}
                            </Typography>
                          }
                          sx={{ display: "flex", alignItems: "flex-start", mb: 0.5, mx: 0 }}
                        />
                      ))}

                      {kpiCheckedCount === 0 ? (
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mt: 1, color: "#81c784", textAlign: "center", fontWeight: 600 }}
                        >
                          No defects flagged — Clear to proceed
                        </Typography>
                      ) : kpiCheckedCount < kpiItems.length ? (
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mt: 1, color: "#ffb74d", textAlign: "center", fontWeight: 600 }}
                        >
                          {kpiCheckedCount} defect{kpiCheckedCount !== 1 ? "s" : ""} flagged — Raise NCR
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block", mt: 1, color: "#f44336", textAlign: "center",
                            fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
                          }}
                        >
                          HOLD — All defect categories triggered — Do not proceed
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* ── Action Buttons ── */}
              {/* BUG-005 + BUG-008 fix: state-driven button visibility — one action at a time */}
              <Box sx={{ width: "100%", maxWidth: 500 }}>
                {taskStatus === "pending" && (
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleStartTask}
                    disabled={actionPending !== null}
                    fullWidth
                    sx={{
                      py: 3,
                      fontSize: "1.2rem",
                      fontWeight: "bold",
                    }}
                    startIcon={
                      actionPending === "start"
                        ? <CircularProgress size={20} color="inherit" />
                        : <Iconify icon="eva:play-circle-outline" />
                    }
                  >
                    {actionPending === "start" ? "Starting…" : `Start ${currentStation}`}
                  </Button>
                )}

                {taskStatus === "in_process" && (
                  <Stack spacing={1.5}>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleMarkComplete}
                      disabled={actionPending !== null}
                      fullWidth
                      sx={{
                        py: 3,
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                      }}
                      startIcon={
                        actionPending === "complete"
                          ? <CircularProgress size={20} color="inherit" />
                          : <Iconify icon="eva:checkmark-circle-2-outline" />
                      }
                    >
                      {actionPending === "complete" ? "Saving…" : `${currentStation} Complete`}
                    </Button>
                    {needsReview && (
                      <Typography variant="caption" sx={{ color: "#ffb74d", textAlign: "center" }}>
                        Open the QCI checklist above before marking this work complete.
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      onClick={() => handlePauseResume("pause")}
                      disabled={actionPending !== null}
                      fullWidth
                      sx={{
                        py: 1.5,
                        fontSize: "1rem",
                        color: "rgba(255,180,0,0.85)",
                        borderColor: "rgba(255,180,0,0.4)",
                        "&:hover": {
                          borderColor: "rgba(255,180,0,0.7)",
                          backgroundColor: "rgba(255,180,0,0.06)",
                        },
                      }}
                      startIcon={
                        actionPending === "pause"
                          ? <CircularProgress size={18} color="inherit" />
                          : <Iconify icon="eva:pause-circle-outline" />
                      }
                    >
                      {actionPending === "pause" ? "Pausing…" : "Pause"}
                    </Button>
                  </Stack>
                )}

                {taskStatus === "loading" && (
                  <Button variant="contained" disabled fullWidth sx={{ py: 3, fontSize: "1.2rem" }}
                    startIcon={<CircularProgress size={20} color="inherit" />}>
                    Reading status…
                  </Button>
                )}

                {taskStatus === "unknown" && (
                  <Button variant="contained" color="warning" onClick={reloadStatus} fullWidth
                    sx={{ py: 3, fontSize: "1.2rem" }}>
                    Status could not be read. Tap to retry
                  </Button>
                )}

                {/* W-21: a recorded pause. Resume before completing. */}
                {taskStatus === "paused" && (
                  <Stack spacing={1}>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => handlePauseResume("resume")}
                      disabled={actionPending !== null}
                      fullWidth
                      sx={{ py: 3, fontSize: "1.2rem", fontWeight: "bold" }}
                      startIcon={
                        actionPending === "resume"
                          ? <CircularProgress size={20} color="inherit" />
                          : <Iconify icon="eva:play-circle-outline" />
                      }
                    >
                      {actionPending === "resume" ? "Resuming…" : `Resume ${currentStation}`}
                    </Button>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", textAlign: "center" }}>
                      Paused. The pause is recorded. Resume before marking this work complete.
                    </Typography>
                  </Stack>
                )}

                {taskStatus === "to_inspect" && (
                  <Button
                    variant="contained"
                    disabled
                    fullWidth
                    sx={{
                      py: 3,
                      fontSize: "1.2rem",
                      fontWeight: "bold",
                      "&.Mui-disabled": {
                        backgroundColor: "rgba(76, 175, 80, 0.3)",
                        color: "#81c784",
                      },
                    }}
                    startIcon={<Iconify icon="eva:checkmark-circle-2-outline" />}
                  >
                    Sent to Inspection ✓
                  </Button>
                )}
              </Box>

              <Button
                variant="contained"
                color="error"
                onClick={() => setFlagIssueModal(true)}
                sx={{
                  width: "100%",
                  maxWidth: 500,
                  py: 2,
                  fontSize: "1.1rem",
                }}
                startIcon={<Iconify icon="eva:alert-circle-outline" />}
              >
                Flag Quality Issue / NCR
              </Button>

              <Button
                variant="outlined"
                onClick={() => {
                  setCurrentItem(null);
                  setStatusNow("pending");
                  setTaskId(null);
                  setScanInput("");
                  scanInputRef.current?.focus();
                }}
                disabled={actionPending !== null}
                sx={{
                  width: "100%",
                  maxWidth: 500,
                  py: 2,
                  color: "rgba(255,255,255,0.7)",
                  borderColor: "rgba(255,255,255,0.25)",
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.5)",
                    backgroundColor: "rgba(255,255,255,0.05)",
                  },
                }}
              >
                ← Scan Next Piece
              </Button>
            </Stack>
          )}
        </Box>

        {/* Footer - Operator Info */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{
            pt: 2,
            borderTop: "1px solid rgba(255,255,255,0.1)",
            color: "rgba(255,255,255,0.6)",
          }}
        >
          <Typography variant="body2">
            Operator: {loginUser?.decodedToken?.firstName} {loginUser?.decodedToken?.lastName}
          </Typography>
          <Typography variant="body2" sx={{ ml: 2 }}>
            {dateString} {timeString}
          </Typography>
        </Stack>
      </Box>

      {/* Flag Issue Modal */}
      <Dialog
        open={flagIssueModal}
        onClose={() => setFlagIssueModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#1a1f3a",
            color: "white",
          },
        }}
      >
        <DialogTitle>Flag Quality Issue — Create NCR</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Piece: <strong>{displayMark(currentItem)}</strong> · Station: <strong>{currentStation}</strong>
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.5)" sx={{ display: "block", mb: 2 }}>
            This will create a Non-Conformance Report (NCR) tied to this piece. Be specific — QC will use this to determine disposition (use-as-is, rework, or scrap).
          </Typography>
          <TextField
            label="Describe the issue (what you found, where, and how bad)"
            value={issueReason}
            onChange={(e) => setIssueReason(e.target.value)}
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            sx={{
              "& .MuiInputBase-input": {
                color: "white",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "rgba(255,255,255,0.2)",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFlagIssueModal(false)}
            variant="outlined"
            disabled={actionPending === "flag"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFlagIssue}
            variant="contained"
            color="error"
            disabled={!issueReason.trim() || actionPending !== null}
            startIcon={
              actionPending === "flag"
                ? <CircularProgress size={16} color="inherit" />
                : null
            }
          >
            {actionPending === "flag" ? "Submitting…" : "Submit NCR"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default KioskView;
